import React from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Sidebar from './components/Sidebar';
import Login from './pages/Login';
import Home from './pages/Home';
import NewOrder from './pages/NewOrder';
import EditOrder from './pages/EditOrder';
import MyOrders from './pages/MyOrders';
import Announcements from './pages/Announcements';
import Contacts from './pages/Contacts';
import FactoryQueue from './pages/FactoryQueue';
import DriverOrders from './pages/DriverOrders';
import PrintOrder from './pages/PrintOrder';
import Shops from './pages/admin/Shops';
import Factories from './pages/admin/Factories';
import Products from './pages/admin/Products';
import Users from './pages/admin/Users';
import Workers from './pages/admin/Workers';
import Export from './pages/admin/Export';

function Layout({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  // Hide sidebar (and the app layout shell) on print pages
  const isPrintRoute = location.pathname.startsWith('/print/');
  if (!user || isPrintRoute) return children;
  return (
    <div className="app-layout">
      <Sidebar />
      <div className="main">{children}</div>
    </div>
  );
}

// The dashboard queries admin-only endpoints, so a driver's landing page is
// his own board instead.
function HomeOrDriverBoard() {
  const { user } = useAuth();
  return user && user.role === 'driver' ? <DriverOrders /> : <Home />;
}

function AppRoutes() {
  return (
    <Layout>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/" element={<ProtectedRoute><HomeOrDriverBoard /></ProtectedRoute>} />

        {/* Shop worker (and admin) */}
        <Route path="/new-order" element={
          <ProtectedRoute roles={['shop_worker', 'super_admin']}><NewOrder /></ProtectedRoute>
        } />
        <Route path="/my-orders" element={
          <ProtectedRoute roles={['shop_worker', 'super_admin']}><MyOrders /></ProtectedRoute>
        } />
        <Route path="/edit-order/:id" element={
          <ProtectedRoute roles={['shop_worker', 'super_admin']}><EditOrder /></ProtectedRoute>
        } />

        {/* Everyone sees announcements; only admins can write them */}
        <Route path="/announcements" element={
          <ProtectedRoute><Announcements /></ProtectedRoute>
        } />

        {/* Contact directory — everyone reads, only admins edit */}
        <Route path="/contacts" element={
          <ProtectedRoute><Contacts /></ProtectedRoute>
        } />

        {/* Factory worker (and admin) */}
        <Route path="/factory-queue" element={
          <ProtectedRoute roles={['factory_worker', 'super_admin']}><FactoryQueue /></ProtectedRoute>
        } />

        {/* Driver — read-only board. The backend refuses this role any
            non-GET request, so there is nothing to guard here beyond the view. */}
        <Route path="/driver-orders" element={
          <ProtectedRoute roles={['driver', 'super_admin']}><DriverOrders /></ProtectedRoute>
        } />

        {/* Print view - any authenticated user can print orders they have access to (backend enforces) */}
        <Route path="/print/:id" element={
          <ProtectedRoute><PrintOrder /></ProtectedRoute>
        } />

        {/* Admin only */}
        <Route path="/admin/shops" element={
          <ProtectedRoute roles={['super_admin']}><Shops /></ProtectedRoute>
        } />
        <Route path="/admin/factories" element={
          <ProtectedRoute roles={['super_admin']}><Factories /></ProtectedRoute>
        } />
        <Route path="/admin/products" element={
          <ProtectedRoute roles={['super_admin']}><Products /></ProtectedRoute>
        } />
        <Route path="/admin/users" element={
          <ProtectedRoute roles={['super_admin']}><Users /></ProtectedRoute>
        } />
        <Route path="/admin/workers" element={
          <ProtectedRoute roles={['super_admin']}><Workers /></ProtectedRoute>
        } />
        <Route path="/admin/export" element={
          <ProtectedRoute roles={['super_admin']}><Export /></ProtectedRoute>
        } />

        {/* Fallback → home */}
        <Route path="*" element={<ProtectedRoute><HomeOrDriverBoard /></ProtectedRoute>} />
      </Routes>
    </Layout>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 3500,
            style: { fontSize: 14 },
          }}
        />
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}
